import { useState, useEffect, useCallback } from 'react';
import {
	Box,
	Button,
	Alert,
	CircularProgress,
	IconButton,
	Tooltip,
	TextField,
	Grid,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Link,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import { useSelector } from 'react-redux';

declare global {
	interface Window {
		handleUPSCallback?: (data: { success?: boolean; error?: string; [key: string]: unknown }) => void | Promise<void>;
	}
}

interface UpsAddress {
	AddressLine?: string;
	City?: string;
	StateProvinceCode?: string;
	PostalCode?: string;
	CountryCode?: string;
}

interface PackageDetails {
	shipperName?: string | undefined;
	shipperAddress?: UpsAddress | undefined;
	recipientName?: string | undefined;
	recipientAddress?: UpsAddress | undefined;
	length?: string;
	width?: string;
	height?: string;
	weight?: string;
	shipperAddressParseError?: string | null | undefined;
	addressParseError?: string | null | undefined;
	[key: string]: unknown;
}

interface ShipperInfo {
	shipperName?: string;
	shipperAddress?: UpsAddress;
	shipperAddressParseError?: string | null | undefined;
}

interface RecipientInfo {
	recipientName?: string;
	recipientAddress?: UpsAddress;
	addressParseError?: string | null | undefined;
}

interface RateItem {
	serviceCode: string;
	serviceName: string;
	totalCharges: string;
	taxAmount?: string;
	deliveryDays?: number | string;
	[key: string]: unknown;
}

interface ShippingUserInfo {
	name?: string;
	address?: string;
	[key: string]: unknown;
}

interface ShippingUserData {
	name?: string;
	address?: string;
	disableShippingInfo?: boolean;
	shippingAddress?: string;
	shippingName?: string;
	[key: string]: unknown;
}

interface ShippingComponentProps {
	userData?: ShippingUserData | undefined;
	onLabelCreated?: (data: { trackingNumber: string; labelImage?: string; service?: string }) => void;
}

interface ParseAddressResult {
	success: boolean;
	error?: string;
	data: {
		street: string;
		city: string;
		state: string;
		zip: string;
	};
}

// Helper function to parse address string
function parseAddress(addressStr: string): ParseAddressResult {
	// Expected format: "60 Arron Smith Drive, Honolulu, HI 96814"
	try {
		const parts = addressStr.split(',').map(part => part.trim());
		if (parts.length !== 3) throw new Error('Invalid address format');

		const streetAddress = parts[0]!;
		const city = parts[1]!;
		const stateZip = parts[2]!.split(' ');

		if (stateZip.length !== 2) throw new Error('Invalid state/zip format');
		if (stateZip[1]!.length !== 5) throw new Error('Invalid ZIP code');

		return {
			success: true,
			data: {
				street: streetAddress,
				city: city,
				state: stateZip[0]!,
				zip: stateZip[1]!
			}
		};
	} catch (error) {
		return {
			success: false,
			error: error instanceof Error ? error.message : 'Invalid address format',
			data: {
				street: addressStr,
				city: '',
				state: '',
				zip: ''
			}
		};
	}
}

export default function ShippingComponent({ userData, onLabelCreated }: ShippingComponentProps) {
	const userInfo = useSelector((state: { auth: { userInfo?: ShippingUserInfo | null } }) => state.auth.userInfo);
	const [isUPSAuthorized, setIsUPSAuthorized] = useState(false);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [authWindow, setAuthWindow] = useState<Window | null>(null);
	const [addressParseError, setAddressParseError] = useState<string | null>(null);
	const [shipperAddressParseError, setShipperAddressParseError] = useState<string | null>(null);
	const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
	const appConfig = useSelector((state: { appConfig: { upsAccountNumber?: string } }) => state.appConfig);

	// Get shipper info based on current user
	const getShipperInfo = useCallback((): ShipperInfo => {
		if (!userInfo) return {};

		const parsedAddress = parseAddress(userInfo.address || '');

		return {
			shipperName: userInfo.name || '',
			shipperAddress: {
				AddressLine: parsedAddress.data.street,
				City: parsedAddress.data.city,
				StateProvinceCode: parsedAddress.data.state,
				PostalCode: parsedAddress.data.zip,
				CountryCode: 'US'
			},
			shipperAddressParseError: !parsedAddress.success ? parsedAddress.error : null
		};
	}, [userInfo]);

	// Get recipient info based on shipping preference
	const getRecipientInfo = useCallback((): RecipientInfo => {
		if (!userData) return {};

		const useShippingInfo = !userData.disableShippingInfo &&
			userData.shippingAddress &&
			userData.shippingName;

		const name = useShippingInfo ? userData.shippingName : userData.name;
		const address = useShippingInfo ? userData.shippingAddress : userData.address;
		const parsedAddress = parseAddress(address || '');

		return {
			recipientName: name || '',
			recipientAddress: {
				AddressLine: parsedAddress.data.street,
				City: parsedAddress.data.city,
				StateProvinceCode: parsedAddress.data.state,
				PostalCode: parsedAddress.data.zip,
				CountryCode: 'US'
			},
			addressParseError: !parsedAddress.success ? parsedAddress.error : null
		};
	}, [userData]);

	// Package details state with pre-filled recipient and shipper info
	const [packageDetails, setPackageDetails] = useState<PackageDetails>(() => ({
		...getShipperInfo(),
		...getRecipientInfo(),
		length: '',
		width: '',
		height: '',
		weight: ''
	}));

	// Update package details and address errors when userData or userInfo changes
	useEffect(() => {
		const shipperInfo = getShipperInfo();
		const recipientInfo = getRecipientInfo();

		setPackageDetails(prev => ({
			...prev,
			shipperName: shipperInfo.shipperName,
			shipperAddress: shipperInfo.shipperAddress,
			recipientName: recipientInfo.recipientName,
			recipientAddress: recipientInfo.recipientAddress
		}));

		setAddressParseError(recipientInfo.addressParseError ?? null);
		setShipperAddressParseError(shipperInfo.shipperAddressParseError ?? null);
	}, [userData, userInfo, getRecipientInfo, getShipperInfo]);

	// Shipping rates state
	const [rates, setRates] = useState<RateItem[]>([]);
	const [loadingRates, setLoadingRates] = useState(false);
	const [ratesError, setRatesError] = useState<string | null>(null);
	const [usingNegotiatedRates, setUsingNegotiatedRates] = useState(false);
	const [labelImage, setLabelImage] = useState<string | null>(null);
	const [trackingNumber, setTrackingNumber] = useState<string | null>(null);
	const [_selectedRate, setSelectedRate] = useState<RateItem | null>(null);
	const [_service, setService] = useState<string | null>(null);

	const checkUPSAuthorization = async () => {
		try {
			setLoading(true);
			const response = await fetch('/api/ups/auth');
			const data = await response.json();

			if (!data.isAuthorized) {
				setIsUPSAuthorized(false);
				// Don't set an error message here, as this is an expected state
				// when the user hasn't authorized yet
				return;
			}

			// If we have authorization, update the state
			setIsUPSAuthorized(true);
			setError(null);
		} catch (_err) {
			setIsUPSAuthorized(false);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		// Check initial authorization status
		void (async () => {
			await checkUPSAuthorization();
			setLoading(false);
		})();

		// Add global callback function for direct access from popup
		window.handleUPSCallback = async (data) => {
			if (data.success) {
				await checkUPSAuthorization();
			} else {
				setIsUPSAuthorized(false);
				setError(data.error || 'Failed to authorize UPS account. Please try again.');
			}
		};

		// Setup message listener for popup window communication
		const handleMessage = async (event: MessageEvent) => {
			// Filter out messages from React DevTools
			if (event.data && typeof event.data === 'object' && event.data.source === 'react-devtools-bridge') {
				return;
			}

			// Verify origin for security
			if (event.origin !== window.location.origin) {
				return;
			}

			if (event.data && event.data.type === 'UPS_AUTH_CALLBACK') {

				if (event.data.success) {
					await checkUPSAuthorization();
				} else {
					setIsUPSAuthorized(false);
					setError(event.data.error || 'Failed to authorize UPS account. Please try again.');
				}

				if (authWindow && !authWindow.closed) {
					authWindow.close();
				}
				setAuthWindow(null);
			}
		};

		// Wrap the async listener in a stable void-returning function so the same
		// reference is used for both add/removeEventListener (and the promise is
		// explicitly fire-and-forget).
		const onMessage = (event: MessageEvent) => { void handleMessage(event); };
		window.addEventListener('message', onMessage);
		return () => {
			window.removeEventListener('message', onMessage);
			// Clean up global callback
			delete window.handleUPSCallback;
		};
	}, [authWindow]);

	const handleAuthorizeUPS = async () => {
		try {
			// Clear any previous errors
			setError(null);

			// Show loading state
			setLoading(true);

			const response = await fetch('/api/ups/auth');
			const data = await response.json();

			// Hide loading state
			setLoading(false);

			// If there's an error message, display it
			if (data.error) {
				setIsUPSAuthorized(false);
				setError(data.error);
				return;
			}

			// If we got an auth URL, open it in a popup
			if (data.authUrl) {
				// First, make sure any previous popup is closed
				if (authWindow && !authWindow.closed) {
					authWindow.close();
				}

				// Open authorization URL in a popup window
				const width = 800;
				const height = 800;
				const left = window.screenX + (window.outerWidth - width) / 2;
				const top = window.screenY + (window.outerHeight - height) / 2;

				// Create a unique name for the popup window
				const popupName = 'UPS_Authorization_' + new Date().getTime();

				const popup = window.open(
					data.authUrl,
					popupName,
					`width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
				);

				// Check if popup was blocked
				if (!popup || popup.closed || typeof popup.closed === 'undefined') {
					setError('Popup was blocked. Please allow popups for this site and try again.');
					return;
				}

				// Try to focus the popup
				popup.focus();

				setAuthWindow(popup);

				// Monitor popup closure
				const checkClosed = setInterval(() => {
					if (popup?.closed) {
						clearInterval(checkClosed);
						setAuthWindow(null);
						// If popup was closed without successful auth, check status
						void checkUPSAuthorization();
					}
				}, 500);

				return;
			}

			// If we get here without an authUrl or error, something went wrong
			setIsUPSAuthorized(false);
			setError('Failed to get UPS authorization URL');
		} catch (_err) {
			setIsUPSAuthorized(false);
			setError('Failed to start UPS authorization process');
			setLoading(false);
		}
	};

	const handlePackageDetailsChange = (field: string, value: string) => {
		if (field.includes('.')) {
			const [parent, child] = field.split('.') as [string, string];
			setPackageDetails(prev => ({
				...prev,
				[parent]: {
					...(prev[parent] as Record<string, unknown>),
					[child]: value
				}
			}));
		} else {
			setPackageDetails(prev => ({
				...prev,
				[field]: value
			}));
		}
	};

	const getNested = (path: string): unknown => path.split('.').reduce<unknown>(
		(obj, key) => (obj as Record<string, unknown> | undefined)?.[key],
		packageDetails,
	);

	// Validation function
	const validateFields = () => {
		const errors: Record<string, string> = {};
		const required: Record<string, string> = {
			shipperName: 'Shipper Name is required',
			'shipperAddress.AddressLine': 'Shipper Address is required',
			'shipperAddress.City': 'Shipper City is required',
			'shipperAddress.StateProvinceCode': 'Shipper State is required',
			'shipperAddress.PostalCode': 'Shipper ZIP Code is required',
			recipientName: 'Recipient Name is required',
			'recipientAddress.AddressLine': 'Recipient Address is required',
			'recipientAddress.City': 'Recipient City is required',
			'recipientAddress.StateProvinceCode': 'Recipient State is required',
			'recipientAddress.PostalCode': 'Recipient ZIP Code is required',
			length: 'Length is required',
			width: 'Width is required',
			height: 'Height is required',
			weight: 'Weight is required'
		};

		// Check required fields
		Object.entries(required).forEach(([field, message]) => {
			const value = field.includes('.')
				? getNested(field)
				: packageDetails[field];

			if (!value || (value as string).trim() === '') {
				errors[field] = message;
			}
		});

		// Validate ZIP codes
		const shipperPostal = packageDetails.shipperAddress?.PostalCode;
		if (shipperPostal && !/^\d{5}$/.test(shipperPostal)) {
			errors['shipperAddress.PostalCode'] = 'Invalid ZIP Code format';
		}
		const recipientPostal = packageDetails.recipientAddress?.PostalCode;
		if (recipientPostal && !/^\d{5}$/.test(recipientPostal)) {
			errors['recipientAddress.PostalCode'] = 'Invalid ZIP Code format';
		}

		// Validate state codes
		const stateRegex = /^[A-Z]{2}$/;
		const shipperState = packageDetails.shipperAddress?.StateProvinceCode;
		if (shipperState && !stateRegex.test(shipperState)) {
			errors['shipperAddress.StateProvinceCode'] = 'Invalid State format (e.g., CA)';
		}
		const recipientState = packageDetails.recipientAddress?.StateProvinceCode;
		if (recipientState && !stateRegex.test(recipientState)) {
			errors['recipientAddress.StateProvinceCode'] = 'Invalid State format (e.g., CA)';
		}

		// Validate dimensions and weight
		['length', 'width', 'height', 'weight'].forEach(field => {
			const value = parseFloat(String((packageDetails[field] as string | number | undefined) ?? ''));
			if (value <= 0) {
				errors[field] = `Invalid ${field}`;
			}
		});

		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleGetRates = async () => {
		if (!validateFields()) {
			setRatesError('Please fix the validation errors before proceeding.');
			return;
		}

		if (!appConfig.upsAccountNumber) {
			setRatesError('Please configure your UPS Account Number in Advanced Settings before proceeding.');
			return;
		}

		setLoadingRates(true);
		setRatesError(null);
		setRates([]);
		setSelectedRate(null);

		try {
			// First check if we're still authorized
			const authCheck = await fetch('/api/ups/auth');
			const authData = await authCheck.json();

			if (!authData.isAuthorized) {
				setIsUPSAuthorized(false);
				throw new Error('UPS authorization has expired. Please reconnect your UPS account.');
			}

			const response = await fetch('/api/ups/rates', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ packageDetails })
			});

			const data = await response.json();

			if (response.status === 401) {
				// Handle unauthorized error
				setIsUPSAuthorized(false);
				throw new Error('UPS authorization has expired. Please reconnect your UPS account.');
			}

			if (data.error) {
				throw new Error(data.error);
			}

			setRates(data.rates);

			// Display information about negotiated rates
			setUsingNegotiatedRates(data.usingNegotiatedRates);
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to get shipping rates';
			setRatesError(message || 'Failed to get shipping rates');

			// If authorization error, show the connect button
			if (message.includes('authorization')) {
				setIsUPSAuthorized(false);
			}
		} finally {
			setLoadingRates(false);
		}
	};

	const handleCreateLabel = async (rate: RateItem) => {
		if (!appConfig.upsAccountNumber) {
			setError('Please configure your UPS Account Number in Advanced Settings before creating a label.');
			return;
		}

		setLoading(true);
		setError(null);
		try {
			// First check if we're still authorized
			const authCheck = await fetch('/api/ups/auth');
			const authData = await authCheck.json();

			if (!authData.isAuthorized) {
				setIsUPSAuthorized(false);
				throw new Error('UPS authorization has expired. Please reconnect your UPS account.');
			}

			const response = await fetch('/api/ups/shipping-label', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					selectedRate: rate,
					packageDetails
				})
			});

			if (response.status === 401) {
				setIsUPSAuthorized(false);
				throw new Error('UPS authorization has expired. Please reconnect your UPS account.');
			}

			const data = await response.json();
			if (data.error) {
				throw new Error(data.error);
			}

			setLabelImage(data.labelImage);
			setTrackingNumber(data.trackingNumber);
			setService(data.service);

			// Call the callback with the label data
			if (onLabelCreated) {
				onLabelCreated({
					trackingNumber: data.trackingNumber,
					labelImage: data.labelImage,
					service: data.service
				});
			}

			// Create a new window to print the label
			const printWindow = window.open('', '_blank');
			if (printWindow) {
				printWindow.document.write(`
					<html>
						<head>
							<title>UPS Shipping Label - ${data.trackingNumber}</title>
							<style>
								body {
									margin: 0;
									display: flex;
									flex-direction: column;
									align-items: center;
									padding: 20px;
								}
								.controls {
									margin-bottom: 20px;
								}
								.print-btn {
									padding: 10px 20px;
									background: #1976d2;
									color: white;
									border: none;
									border-radius: 4px;
									cursor: pointer;
									margin-right: 10px;
								}
								.close-btn {
									padding: 10px 20px;
									background: #dc3545;
									color: white;
									border: none;
									border-radius: 4px;
									cursor: pointer;
								}
								@media print {
									.controls { display: none; }
									img {
										width: 100%;
										height: auto;
									}
								}
							</style>
						</head>
						<body>
							<div class="controls">
								<button class="print-btn" onclick="window.print()">Print Label</button>
								<button class="close-btn" onclick="window.close()">Close Window</button>
							</div>
							<img src="data:image/gif;base64,${data.labelImage}" style="max-width: 100%; height: auto;" />
						</body>
					</html>
				`);
				printWindow.document.close();
			}

		} catch (err) {
			const message = err instanceof Error ? err.message : 'Failed to create shipping label';
			setError(message || 'Failed to create shipping label');

			// If authorization error, show the connect button
			if (message.includes('authorization')) {
				setIsUPSAuthorized(false);
			}
		} finally {
			setLoading(false);
		}
	};

	// Modify TextField to include error handling
	const renderTextField = (label: string, field: string, type: string = 'text') => (
		<TextField
			label={label}
			type={type}
			fullWidth
			value={(field.includes('.')
				? getNested(field)
				: packageDetails[field]) ?? ''
			}
			onChange={(e) => handlePackageDetailsChange(field, e.target.value)}
			error={!!validationErrors[field]}
			helperText={validationErrors[field]}
			margin="dense"
			size="small"
		/>
	);

	// Add this to the render section where rates are displayed
	const renderRatesSection = () => (
		<Box sx={{ mt: 2, mb: 2 }}>
			{loadingRates ? (
				<Box sx={{ p: 2, textAlign: 'center' }}>
					<CircularProgress size={24} />
					<Typography sx={{ mt: 1 }}>Loading rates...</Typography>
				</Box>
			) : ratesError ? (
				<Box sx={{ p: 2, color: 'error.main' }}>{ratesError}</Box>
			) : rates.length > 0 ? (
				<>
					<Box sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						mb: 2,
						px: 1
					}}>
						<Typography variant="h6">Available Shipping Options</Typography>
						{usingNegotiatedRates ? (
							<Box sx={{
								bgcolor: 'success.light',
								color: 'success.contrastText',
								p: '4px 8px',
								borderRadius: 1,
								fontSize: '0.75rem',
								fontWeight: 'bold'
							}}>
								Using Negotiated Rates
							</Box>
						) : (
							<Box sx={{
								bgcolor: 'info.light',
								color: 'info.contrastText',
								p: '4px 8px',
								borderRadius: 1,
								fontSize: '0.75rem',
								fontWeight: 'bold'
							}}>
								Using Standard Rates
							</Box>
						)}
					</Box>
					<Paper sx={{ mb: 2 }}>
						<TableContainer>
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Service</TableCell>
										<TableCell align="right">Base Cost</TableCell>
										<TableCell align="right">Tax</TableCell>
										<TableCell align="right">Total</TableCell>
										<TableCell align="right">Delivery</TableCell>
										<TableCell align="right">Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{rates.map((rate) => (
										<TableRow key={rate.serviceCode} hover>
											<TableCell>
												{rate.serviceName}
											</TableCell>
											<TableCell align="right">
												${(parseFloat(rate.totalCharges) - parseFloat(rate.taxAmount || '0')).toFixed(2)}
											</TableCell>
											<TableCell align="right">
												${parseFloat(rate.taxAmount || '0').toFixed(2)}
											</TableCell>
											<TableCell align="right" sx={{ fontWeight: 'bold' }}>
												${parseFloat(rate.totalCharges).toFixed(2)}
											</TableCell>
											<TableCell align="right">
												{rate.deliveryDays ? `${rate.deliveryDays} days` : 'N/A'}
											</TableCell>
											<TableCell align="right">
												<Button
													variant="outlined"
													size="small"
													startIcon={<PrintIcon />}
													onClick={() => { void handleCreateLabel(rate); }}
												>
													Create Label
												</Button>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					</Paper>
					<Typography
                        variant="caption"
                        sx={{
                            color: "text.secondary",
                            display: 'block',
                            textAlign: 'right',
                            px: 1
                        }}>
						* Prices may vary slightly from UPS.com due to real-time rate changes and promotions
					</Typography>
				</>
			) : (
				<Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
					No shipping rates available
				</Box>
			)}
		</Box>
	);

	if (loading) {
		return (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
				<CircularProgress size={20} />
				<span>Checking UPS authorization...</span>
			</Box>
		);
	}

	return (
        <>
            {/* UPS Authorization Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
				{error ? (
					<Alert
						severity="error"
						sx={{ flex: 1 }}
						action={
							<IconButton
								color="inherit"
								size="small"
								onClick={() => { void checkUPSAuthorization(); }}
							>
								<RefreshIcon fontSize="inherit" />
							</IconButton>
						}
					>
						{error}
					</Alert>
				) : isUPSAuthorized ? (
					<Alert
						severity="success"
						sx={{ flex: 1 }}
						action={
							<Tooltip title="Re-authorize UPS Account">
								<IconButton
									color="inherit"
									size="small"
									onClick={() => { void handleAuthorizeUPS(); }}
								>
									<RefreshIcon fontSize="inherit" />
								</IconButton>
							</Tooltip>
						}
					>
						UPS account is connected
					</Alert>
				) : (
					<>
						<Button
							variant="outlined"
							size="small"
							onClick={() => { void handleAuthorizeUPS(); }}
							startIcon={<LocalShippingIcon />}
						>
							Connect UPS Account
						</Button>
					</>
				)}
			</Box>
            {/* UPS Account Number Status */}
            {isUPSAuthorized && (
				<Box sx={{ my: 1 }}>
					{!appConfig.upsAccountNumber ? (
						<Alert severity="warning">
							Please setup your UPS account number to create shipping labels.
							<br />
							<br />
							You can find it in your <a href="https://wwwapps.ups.com/ppc/ppc.html?loc=en_US#/payment" target="_blank" rel="noopener noreferrer">UPS profile</a> under <b>Payment Options</b> or on a recent <b>UPS invoice</b>.
							<br />
							It{'\''}s a 6-digit alphanumeric code (e.g., A1B2C3).
							<br />
							<br />
							Please fill it in the <Link href="/management/settings">Advanced Settings</Link> page.
						</Alert>
					) : (
						<Alert severity="info">
							Using UPS Account: {appConfig.upsAccountNumber}
						</Alert>
					)}
				</Box>
			)}
            {/* Show shipper address parse error if exists */}
            {shipperAddressParseError && (
				<Alert
					severity="warning"
					sx={{ mt: 2 }}
				>
					Shipper address format error: {shipperAddressParseError}. Please review and update the shipper address manually.
				</Alert>
			)}
            {/* Show recipient address parse error if exists */}
            {addressParseError && (
				<Alert
					severity="warning"
					sx={{ mt: 2 }}
				>
					Recipient address format error: {addressParseError}. Please review and update the recipient address manually.
				</Alert>
			)}
            {/* Shipping Calculator UI - only show when authorized */}
            {isUPSAuthorized && (
				<Paper sx={{ p: 2, mt: 2 }}>
					<Typography variant="h6" gutterBottom>
						Shipping Calculator
					</Typography>

					<Grid container spacing={2}>
						{/* Shipper Information */}
						<Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}>
							<Typography variant="subtitle1" gutterBottom>
								From
							</Typography>
							{renderTextField('Shipper Name', 'shipperName')}
							{renderTextField('Address', 'shipperAddress.AddressLine')}
							<Grid container spacing={1}>
								<Grid size={6}>
									{renderTextField('City', 'shipperAddress.City')}
								</Grid>
								<Grid size={3}>
									{renderTextField('State', 'shipperAddress.StateProvinceCode')}
								</Grid>
								<Grid size={3}>
									{renderTextField('ZIP', 'shipperAddress.PostalCode')}
								</Grid>
							</Grid>
						</Grid>

						{/* Recipient Information */}
						<Grid
                            size={{
                                xs: 12,
                                md: 6
                            }}>
							<Typography variant="subtitle1" gutterBottom>
								To
							</Typography>
							{renderTextField('Recipient Name', 'recipientName')}
							{renderTextField('Address', 'recipientAddress.AddressLine')}
							<Grid container spacing={1}>
								<Grid size={6}>
									{renderTextField('City', 'recipientAddress.City')}
								</Grid>
								<Grid size={3}>
									{renderTextField('State', 'recipientAddress.StateProvinceCode')}
								</Grid>
								<Grid size={3}>
									{renderTextField('ZIP', 'recipientAddress.PostalCode')}
								</Grid>
							</Grid>
						</Grid>

						{/* Package Dimensions */}
						<Grid size={12}>
							<Typography variant="subtitle1" gutterBottom>
								Package Details
							</Typography>
							<Grid container spacing={2}>
								<Grid size={3}>
									{renderTextField('Length (in)', 'length', 'number')}
								</Grid>
								<Grid size={3}>
									{renderTextField('Width (in)', 'width', 'number')}
								</Grid>
								<Grid size={3}>
									{renderTextField('Height (in)', 'height', 'number')}
								</Grid>
								<Grid size={3}>
									{renderTextField('Weight (lbs)', 'weight', 'number')}
								</Grid>
							</Grid>
						</Grid>

						{/* Get Rates Button */}
						<Grid size={12}>
							<Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
								<Button
									variant="contained"
									onClick={() => { void handleGetRates(); }}
									disabled={loadingRates}
								>
									{loadingRates ? 'Getting Rates...' : 'Get Shipping Rates'}
								</Button>
							</Box>
						</Grid>

						{/* Rates Display */}
						{renderRatesSection()}

						{/* Label Display */}
						{labelImage && (
							<Grid size={12}>
								<Paper sx={{ p: 2, mt: 2 }}>
									<Typography variant="subtitle1" gutterBottom>
										Tracking Number: {trackingNumber}
									</Typography>
									<img
										src={`data:image/gif;base64,${labelImage}`}
										alt="Shipping Label"
										style={{ maxWidth: '100%' }}
									/>
								</Paper>
							</Grid>
						)}
					</Grid>
				</Paper>
			)}
        </>
    );
}
